
import { useState, useMemo } from 'react';
import { myAiProjects } from '../../../mocks/myAiProjects';

type TimeRange = '7d' | '30d' | '90d' | 'all';

interface DailyData {
  date: string;
  views: number;
  likes: number;
  comments: number;
}

const generateTrendData = (days: number): DailyData[] => {
  const data: DailyData[] = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const dayOfWeek = date.getDay();
    const weekendBoost = dayOfWeek === 0 || dayOfWeek === 6 ? 1.4 : 1;
    const trendMultiplier = 1 + (days - i) / days * 0.5;
    data.push({
      date: date.toISOString().split('T')[0],
      views: Math.floor((Math.random() * 800 + 200) * weekendBoost * trendMultiplier),
      likes: Math.floor((Math.random() * 120 + 30) * weekendBoost * trendMultiplier),
      comments: Math.floor((Math.random() * 40 + 5) * weekendBoost * trendMultiplier),
    });
  }
  return data;
};

const CreatorAnalyticsDashboard = () => {
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [activeMetric, setActiveMetric] = useState<'views' | 'likes' | 'comments'>('views');

  const publishedProjects = useMemo(
    () => myAiProjects.filter((p) => p.status === 'published'),
    [],
  );

  const totalViews = publishedProjects.reduce((sum, p) => sum + p.views, 0);
  const totalLikes = publishedProjects.reduce((sum, p) => sum + p.likes, 0);
  const totalComments = publishedProjects.reduce((sum, p) => sum + p.comments, 0);
  const engagementRate = totalViews > 0 ? ((totalLikes + totalComments) / totalViews * 100).toFixed(1) : '0';

  const daysMap: Record<TimeRange, number> = { '7d': 7, '30d': 30, '90d': 90, all: 180 };
  const trendData = useMemo(() => generateTrendData(daysMap[timeRange]), [timeRange]);

  const maxValue = useMemo(() => {
    return Math.max(...trendData.map((d) => d[activeMetric]), 1);
  }, [trendData, activeMetric]);

  const currentPeriodTotal = trendData.reduce((sum, d) => sum + d[activeMetric], 0);
  const halfLen = Math.floor(trendData.length / 2);
  const firstHalf = trendData.slice(0, halfLen).reduce((s, d) => s + d[activeMetric], 0);
  const secondHalf = trendData.slice(halfLen).reduce((s, d) => s + d[activeMetric], 0);
  const growthPercent = firstHalf > 0 ? (((secondHalf - firstHalf) / firstHalf) * 100).toFixed(1) : '0';
  const isGrowthPositive = Number(growthPercent) >= 0;

  const metricColors: Record<string, { bar: string; bg: string; text: string; gradient: string }> = {
    views: { bar: 'bg-amber-400', bg: 'bg-amber-50', text: 'text-amber-600', gradient: 'from-amber-400 to-orange-400' },
    likes: { bar: 'bg-rose-400', bg: 'bg-rose-50', text: 'text-rose-600', gradient: 'from-rose-400 to-pink-400' },
    comments: { bar: 'bg-emerald-400', bg: 'bg-emerald-50', text: 'text-emerald-600', gradient: 'from-emerald-400 to-teal-400' },
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Show a subset of x-axis labels
  const labelInterval = trendData.length <= 7 ? 1 : trendData.length <= 30 ? 5 : 15;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <i className="ri-line-chart-line text-amber-500"></i>
            Creator Analytics
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Performance overview across {publishedProjects.length} published project{publishedProjects.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center bg-gray-100 rounded-lg p-1">
          {(['7d', '30d', '90d', 'all'] as TimeRange[]).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                timeRange === range
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {range === 'all' ? 'All Time' : range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Views', value: totalViews, icon: 'ri-eye-line', color: 'from-amber-400 to-orange-400', change: '+12.3%' },
          { label: 'Total Likes', value: totalLikes, icon: 'ri-heart-line', color: 'from-rose-400 to-pink-400', change: '+8.7%' },
          { label: 'Total Comments', value: totalComments, icon: 'ri-chat-3-line', color: 'from-emerald-400 to-teal-400', change: '+15.2%' },
          { label: 'Engagement Rate', value: engagementRate, icon: 'ri-fire-line', color: 'from-orange-400 to-red-400', change: '+2.1%', suffix: '%' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                <i className={`${stat.icon} text-white text-sm`}></i>
              </div>
              <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                {stat.change}
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900 tabular-nums">
              {typeof stat.value === 'number' ? formatNumber(stat.value) : stat.value}{stat.suffix || ''}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Trend Chart */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h4 className="text-sm font-semibold text-gray-900">Engagement Trends</h4>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xl font-bold text-gray-900 tabular-nums">{formatNumber(currentPeriodTotal)}</span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                isGrowthPositive ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
              }`}>
                {isGrowthPositive ? '+' : ''}{growthPercent}%
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {(['views', 'likes', 'comments'] as const).map((metric) => (
              <button
                key={metric}
                onClick={() => setActiveMetric(metric)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap capitalize ${
                  activeMetric === metric
                    ? `bg-white ${metricColors[metric].text} shadow-sm`
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {metric}
              </button>
            ))}
          </div>
        </div>

        {/* Bar Chart */}
        <div className="relative h-48">
          <div className="absolute inset-0 flex items-end gap-[2px]">
            {trendData.map((d, i) => {
              const height = (d[activeMetric] / maxValue) * 100;
              return (
                <div
                  key={i}
                  className="flex-1 group relative flex flex-col items-center justify-end"
                >
                  <div
                    className={`w-full rounded-t-sm ${metricColors[activeMetric].bar} opacity-80 hover:opacity-100 transition-all cursor-pointer min-h-[2px]`}
                    style={{ height: `${Math.max(height, 1)}%` }}
                  ></div>
                  {/* Tooltip */}
                  <div className="absolute bottom-full mb-2 hidden group-hover:block z-10 pointer-events-none">
                    <div className="bg-gray-900 text-white rounded-lg px-3 py-2 text-xs whitespace-nowrap shadow-lg">
                      <p className="font-medium">{formatDate(d.date)}</p>
                      <p className="text-gray-300 capitalize">{activeMetric}: {d[activeMetric].toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Y-axis guides */}
          <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
            {[1, 0.75, 0.5, 0.25, 0].map((ratio) => (
              <div key={ratio} className="flex items-center gap-2">
                <span className="text-[10px] text-gray-400 w-8 text-right tabular-nums">
                  {formatNumber(Math.round(maxValue * ratio))}
                </span>
                <div className="flex-1 border-t border-dashed border-gray-100"></div>
              </div>
            ))}
          </div>
        </div>
        {/* X-axis labels */}
        <div className="flex mt-2 pl-10">
          {trendData.map((d, i) => (
            <div key={i} className="flex-1 text-center">
              {i % labelInterval === 0 && (
                <span className="text-[10px] text-gray-400">{formatDate(d.date)}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Project Performance Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <i className="ri-movie-2-line text-amber-500"></i>
            Project Performance
          </h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                <th className="text-left px-5 py-3 font-medium">Project</th>
                <th className="text-right px-5 py-3 font-medium">Views</th>
                <th className="text-right px-5 py-3 font-medium">Likes</th>
                <th className="text-right px-5 py-3 font-medium">Comments</th>
                <th className="text-right px-5 py-3 font-medium">Engagement</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {publishedProjects.length > 0 ? (
                publishedProjects
                  .sort((a, b) => b.views - a.views)
                  .map((project) => {
                    const eng = project.views > 0
                      ? ((project.likes + project.comments) / project.views * 100).toFixed(1)
                      : '0';
                    return (
                      <tr key={project.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-8 rounded overflow-hidden flex-shrink-0">
                              <img
                                src={project.thumbnail}
                                alt={project.title}
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">{project.title}</p>
                              <p className="text-[11px] text-gray-400">{project.category}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <span className="text-sm font-medium text-gray-900 tabular-nums">{formatNumber(project.views)}</span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <span className="text-sm font-medium text-gray-900 tabular-nums">{formatNumber(project.likes)}</span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <span className="text-sm font-medium text-gray-900 tabular-nums">{formatNumber(project.comments)}</span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <span className={`text-sm font-semibold tabular-nums ${
                            Number(eng) >= 10 ? 'text-emerald-600' : Number(eng) >= 5 ? 'text-amber-600' : 'text-gray-600'
                          }`}>
                            {eng}%
                          </span>
                        </td>
                      </tr>
                    );
                  })
              ) : (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-gray-400 text-sm">
                    No published projects yet. Publish a project to see analytics.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Performing Insights */}
      {publishedProjects.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Most Viewed */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-100 p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center">
                <i className="ri-eye-line text-amber-600 text-sm"></i>
              </div>
              <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Most Viewed</span>
            </div>
            {(() => {
              const top = [...publishedProjects].sort((a, b) => b.views - a.views)[0];
              return (
                <div>
                  <p className="text-sm font-semibold text-gray-900 line-clamp-1">{top.title}</p>
                  <p className="text-xl font-bold text-amber-600 mt-1 tabular-nums">{formatNumber(top.views)} views</p>
                </div>
              );
            })()}
          </div>

          {/* Most Liked */}
          <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl border border-rose-100 p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-rose-100 flex items-center justify-center">
                <i className="ri-heart-line text-rose-600 text-sm"></i>
              </div>
              <span className="text-xs font-semibold text-rose-700 uppercase tracking-wide">Most Liked</span>
            </div>
            {(() => {
              const top = [...publishedProjects].sort((a, b) => b.likes - a.likes)[0];
              return (
                <div>
                  <p className="text-sm font-semibold text-gray-900 line-clamp-1">{top.title}</p>
                  <p className="text-xl font-bold text-rose-600 mt-1 tabular-nums">{formatNumber(top.likes)} likes</p>
                </div>
              );
            })()}
          </div>

          {/* Most Discussed */}
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-100 p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center">
                <i className="ri-chat-3-line text-emerald-600 text-sm"></i>
              </div>
              <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Most Discussed</span>
            </div>
            {(() => {
              const top = [...publishedProjects].sort((a, b) => b.comments - a.comments)[0];
              return (
                <div>
                  <p className="text-sm font-semibold text-gray-900 line-clamp-1">{top.title}</p>
                  <p className="text-xl font-bold text-emerald-600 mt-1 tabular-nums">{formatNumber(top.comments)} comments</p>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorAnalyticsDashboard;
